import { notificationError } from 'core/utils';
import _ from 'lodash';
import { action, makeObservable } from 'mobx';
import AddressAutofillForm from 'modules/order/forms/AddressAutofillForm';
import { AddressCompletionStore } from 'modules/order/stores';
import BaasicDropdownViewStore from './BaasicDropdownViewStore';

class AddressAutofillViewStore {
	debouncedAutofillSearch = null;
	form = new AddressAutofillForm();
	isDropdownFocused = false;

	constructor(rootStore, { additionalEvents, parentViewData }) {
		makeObservable(this);
		this.rootStore = rootStore;
		this.parentViewData = parentViewData;
		this.additionalEvents = additionalEvents;
		this.addressCompletionStore = new AddressCompletionStore(rootStore);
		this.debouncedAutofillSearch = _.debounce(value => {
			this.searchAddressAutofill(value);
		}, 500);
		this.oldData = null;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.addressAutofillDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('addressAutofill'),
			store: new AddressCompletionStore(this.rootStore),
			fetchFunc: 'findAutofill',
			fetchAll: true,
			fetchInitialValues: false,
			onChangeCallback: value => this.onSelectAutofillChange(value),
			onBlur: () => this.onAutofillDropdownFocusChange(false),
			itemTemplate: {
				value: 'label',
				label: 'label',
			},
		});
	};

	@action.bound
	onAutofillDropdownFocusChange = async isFocused => {
		this.form.$('displayDropdown').value = isFocused;
	};

	@action.bound
	onAddressInputFocusChange = async showDropdown => {
		this.form.$('displayDropdown').value = showDropdown;
		if (showDropdown == false) {
			// onBlur events
			if (this.parentViewData.address1 && this.parentViewData.address1.value) {
				this.parentViewData.address1.value = this.parentViewData.address1.value.trim();
			}
		} else {
			// onFocus events
			if (this.isFilterChanged(this.oldFilter, this.getFilter(this.parentViewData.address1.value)))
				await this.searchAddressAutofill(this.parentViewData.address1.value);
		}
	};

	@action.bound
	searchAddressAutofill = async value => {
		try {
			if (value && value != '') {
				this.form.$('displayDropdown').value = true;
				let filter = this.getFilter(value);
				await this.addressAutofillDropdownStore.updateFilter(filter);
				this.oldFilter = filter;
				if (this.additionalEvents && this.additionalEvents.onInputChange) {
					await this.additionalEvents.onInputChange();
				}
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	onSelectAutofillChange = async selectedOption => {
		try {
			this.setAddressFormValues(selectedOption.extra);
			this.form.$('displayDropdown').value = false;
			const response = await this.addressCompletionStore.verifyAddress({
				recipient: this.parentViewData.buyerName ? this.parentViewData.buyerName.value : '',
				line1: this.parentViewData.address1.value,
				line2: this.parentViewData.address2.value,
				city: this.parentViewData.city.value,
				postalOrZip: this.parentViewData.zipCode.value,
				provinceOrState: this.getState(),
				country: this.getCountry(),
			});
			if (response) {
				this.setAddressFormValues(response);
				this.rootStore.notificationStore.success(['ADDRESSAUTOFILL.VERIFYSUCCESS']);

				if (this.additionalEvents && this.additionalEvents.onInputChange) {
					await this.additionalEvents.onInputChange();
				}
			}
		} catch (err) {
			if (err.data == null && err.statusCode == 400) {
				this.rootStore.notificationStore.error(['ADDRESSAUTOFILL.VERIFYFAILED']);
			} else if (err.data != null && err.statusCode == 400 && err.data.errorMessage != '') {
				this.rootStore.notificationStore.error('Address verification error: ' + err.data.errorMessage);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	isFilterChanged = (oldFilter, newFilter) => {
		for (const key in oldFilter) {
			if (oldFilter.hasOwnProperty(key) && oldFilter[key] !== newFilter[key]) {
				return true; // Something has changed
			}
		}
		return false; // If no differences were found
	};

	getFilter = streetAddress => {
		return {
			line1: streetAddress,
			city: this.parentViewData.city.value,
			postalOrZip: '', // ZipCode filter doesn't work properly on suggestions
			provinceOrState: this.getState(),
			country: this.getCountry(),
		};
	};

	@action.bound
	getState = () => {
		if (this.parentViewData.stateCode) {
			return this.parentViewData.stateCode.value;
		} else {
			if (this.parentViewData.isBaasicDropdown) {
				if (this.parentViewData.stateDropdownStore.value) {
					return this.parentViewData.stateDropdownStore.value.extra.countryStateCode;
				}
			} else {
				if (this.parentViewData.state.value) {
					return this.parentViewData.state.value.extra.countryStateCode;
				}
			}
		}
	};
	@action.bound
	getCountry = () => {
		if (this.parentViewData.countryCode) {
			return this.parentViewData.countryCode.value;
		} else {
			if (this.parentViewData.isBaasicDropdown) {
				if (this.parentViewData.countryDropdownStore.value) {
					return this.parentViewData.countryDropdownStore.value.extra.countryCode;
				}
			} else {
				if (this.parentViewData.country.value) {
					return this.parentViewData.country.value.extra.countryCode;
				}
			}
		}
	};

	setAddressFormValues(item) {
		this.parentViewData.address1.value = item.address ? item.address.trim() : '';
		this.parentViewData.address2.value = item.address2 ? item.address2 : '';
		this.parentViewData.zipCode.value = item.postalCode ? item.postalCode : '';
		this.parentViewData.city.value = item.city ? item.city : '';
		if (this.parentViewData.buyerName && item.firmName && item.firmName != '') {
			this.parentViewData.buyerName.value = item.firmName;
		}
		// Sets State and CountryCode
		if (this.parentViewData.stateCode && this.parentViewData.countryCode) {
			this.parentViewData.stateCode.value = item.stateCode ? item.stateCode : '';
			this.parentViewData.countryCode.value = item.countryCode ? item.countryCode : '';
		} else {
			if (this.parentViewData.isBaasicDropdown) {
				this.parentViewData.stateDropdownStore.setValue(item.coreCountryState);
				this.parentViewData.countryDropdownStore.setValue(item.coreCountry);
			} else {
				const coreCountryState = item.coreCountryState
					? {
							value: item.coreCountryState.id,
							label: item.coreCountryState.name,
							extra: item.coreCountryState,
					  }
					: null;
				this.parentViewData.state.set(coreCountryState);
				const coreCountry = item.coreCountry
					? {
							value: item.coreCountry.id,
							label: item.coreCountry.name,
							extra: item.coreCountry,
					  }
					: null;
				this.parentViewData.country.set('value', coreCountry);
			}
		}
	}

	@action.bound
	handleKeyDown = e => {
		const activeOptionElement = document.querySelector('.react-select__option--is-focused');
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			if (activeOptionElement) {
				const siblingKey = e.key === 'ArrowDown' ? 'nextElementSibling' : 'previousElementSibling';
				const nextOption = activeOptionElement[siblingKey];

				if (nextOption) {
					activeOptionElement.classList.remove('react-select__option--is-focused');
					activeOptionElement.style.backgroundColor = 'white'; // Removes blue highlited option
					nextOption.classList.add('react-select__option--is-focused');
					nextOption.scrollIntoView({ block: 'nearest', inline: 'nearest' });
				}
			} else {
				const firstOption = document.querySelector('.react-select__option');
				if (firstOption) {
					firstOption.classList.add('react-select__option--is-focused');
				}
			}
		} else if (e.key === 'Enter') {
			e.preventDefault();
			if (activeOptionElement) {
				const optionValue = activeOptionElement.querySelector('span').innerText;
				const selectedOption = this.addressAutofillDropdownStore.options.find(option => option.label === optionValue);
				if (selectedOption) {
					this.addressAutofillDropdownStore.setValue(selectedOption);
				}
			}
		}
	};
}

export default AddressAutofillViewStore;



// WEBPACK FOOTER //
// ./src/common/stores/AddressAutofillViewStore.js